import React from 'react';

import AtIcon from 'svg/AtIcon';
import AttachFileIcon from 'svg/AttachFileIcon';
import BellIcon from 'svg/BellIcon';
import CalendarCheckIcon from 'svg/CalendarCheckIcon';
import CalendarClockIcon from 'svg/CalendarClockIcon';
import ChatBubbleIcon from 'svg/ChatBubbleIcon';
import CircleCheckOutlineIcon from 'svg/CircleCheckOutlineIcon';
import CircleReturnArrowIcon from 'svg/CircleReturnArrowIcon';
import ClipboardLinesIcon from 'svg/ClipboardLinesIcon';
import CoordinateRequestOutlineIcon from 'svg/CoordinateRequestOutlineIcon';
import HourglassIcon from 'svg/HourglassIcon';
import LocationPinIcon from 'svg/LocationPinIcon';
import PatientAddedToQueueIcon from 'svg/PatientAddedToQueueIcon';
import PatientDischargedIcon from 'svg/PatientDischargedIcon';
import PersonProhibitedIcon from 'svg/PersonProhibitedIcon';
import PriorityFlag from 'svg/PriorityFlag';
import ReferralOutlineIcon from 'svg/ReferralOutlineIcon';
import SquareExclamationIcon from 'svg/SquareExclamationIcon';
import ThumbsDownOutlineIcon from 'svg/ThumbsDownOutlineIcon';
import ThumbsUpOutlineIcon from 'svg/ThumbsUpOutlineIcon';
import WarnIcon from 'svg/WarnIcon';

// The complete chip palette (OLIO-3059 ICON_MAP.md): every kind's chip is one of exactly three
// brand hues. These are design-sourced values (from Taylor's exported glyphs/rects), NOT the app's
// semantic theme colors — the notification chips are their own closed palette.
export const CHIP_COLORS = {
  blue: '#1283DA',
  green: '#11AF22',
  red: '#D93F4C',
} as const;

// Semantic tone drives the chip color: positive acknowledgements are green, negative events
// (denials, readmissions, refusals, off-track) are red, and everything else — informational and
// time-sensitive alike — is blue. The chip is a rounded square with the kind's color at 10%
// opacity behind the glyph (ICON_MAP.md chip recipe).
export enum NotificationTone {
  Info = 'info',
  Positive = 'positive',
  Negative = 'negative',
}

type ToneColors = { color: string; background: string };

const TONE_COLORS: Record<NotificationTone, ToneColors> = {
  [NotificationTone.Info]: { color: CHIP_COLORS.blue, background: 'rgba(18, 131, 218, 0.1)' },
  [NotificationTone.Positive]: { color: CHIP_COLORS.green, background: 'rgba(17, 175, 34, 0.1)' },
  [NotificationTone.Negative]: { color: CHIP_COLORS.red, background: 'rgba(217, 63, 76, 0.1)' },
};

type NotificationTypeConfig = {
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  tone: NotificationTone;
};

// `kind` → chip icon + tone. Keyed by the NotificationEvent.kind the API sends (Appendix A of the
// tech design; the five previously-empty Figma icon groups now use Taylor's exported glyphs per
// ICON_MAP.md). Unknown/new kinds fall back to a neutral bell (DEFAULT_NOTIFICATION_TYPE), so a
// renderer shipped ahead of a FE map entry still renders a sensible row (wave-2 safety).
export const NOTIFICATION_TYPES: Record<string, NotificationTypeConfig> = {
  // mention
  new_mention: { Icon: AtIcon, tone: NotificationTone.Info },
  new_workflow_action_mention: { Icon: AtIcon, tone: NotificationTone.Info },
  new_chat_mention: { Icon: ChatBubbleIcon, tone: NotificationTone.Info },

  // critical_alerts — acknowledgements (positive)
  escalation_acknowledgement: { Icon: CircleCheckOutlineIcon, tone: NotificationTone.Positive },
  priority_note_acknowledgement: { Icon: CircleCheckOutlineIcon, tone: NotificationTone.Positive },
  // Same circled return-arrow as safe_return_requested (Taylor's exports carried the glyph twice —
  // once per safe-return kind; resolved 2026-08-31, superseding the clipboard-check provisional).
  safe_return_acknowledged: { Icon: CircleReturnArrowIcon, tone: NotificationTone.Positive },

  // critical_alerts — escalations / requests
  escalations: { Icon: WarnIcon, tone: NotificationTone.Negative },
  priority_note_added: { Icon: PriorityFlag, tone: NotificationTone.Negative },
  // ICON_MAP glyph: circled return arrow (green).
  safe_return_requested: { Icon: CircleReturnArrowIcon, tone: NotificationTone.Positive },
  // ICON_MAP glyph: square + exclamation (red).
  readmission: { Icon: SquareExclamationIcon, tone: NotificationTone.Negative },
  // ICON_MAP glyph: person + prohibition sign (red).
  refusal_of_home_health_services: { Icon: PersonProhibitedIcon, tone: NotificationTone.Negative },

  // critical_alerts — time-sensitive dates (blue: informational, per the calendar-clock glyph)
  initial_authorization_nearing_valid_through_date: { Icon: HourglassIcon, tone: NotificationTone.Info },
  // ICON_MAP glyph: calendar + clock (blue) — shared by both "nearing date" kinds.
  patient_nearing_next_review_date: { Icon: CalendarClockIcon, tone: NotificationTone.Info },
  patient_nearing_projected_discharge: { Icon: CalendarClockIcon, tone: NotificationTone.Info },

  // critical_alerts / activity — informational
  patient_moved_to_admitted_and_in_treatment: { Icon: LocationPinIcon, tone: NotificationTone.Info },
  patient_added_to_queue: { Icon: PatientAddedToQueueIcon, tone: NotificationTone.Info },
  patient_discharged: { Icon: PatientDischargedIcon, tone: NotificationTone.Info },
  // Calendar + checkmark, green (Taylor, 2026-08-31): the calendar base with a check badge.
  authorization_number_added: { Icon: CalendarCheckIcon, tone: NotificationTone.Positive },
  new_document_attached: { Icon: AttachFileIcon, tone: NotificationTone.Info },
  new_information_added: { Icon: ClipboardLinesIcon, tone: NotificationTone.Info },
  coordinate_request_submitted: { Icon: CoordinateRequestOutlineIcon, tone: NotificationTone.Info },

  // referrals
  referral_new_information_added: { Icon: ClipboardLinesIcon, tone: NotificationTone.Info },
  referral_created: { Icon: ReferralOutlineIcon, tone: NotificationTone.Info },
  referral_accepted: { Icon: ReferralOutlineIcon, tone: NotificationTone.Positive },
  referral_denied: { Icon: ReferralOutlineIcon, tone: NotificationTone.Negative },

  // patient_status_changes
  status_improving: { Icon: ThumbsUpOutlineIcon, tone: NotificationTone.Positive },
  status_declining: { Icon: ThumbsDownOutlineIcon, tone: NotificationTone.Negative },
};

export const DEFAULT_NOTIFICATION_TYPE: NotificationTypeConfig = {
  Icon: BellIcon,
  tone: NotificationTone.Info,
};

export type NotificationVisual = { Icon: React.FC<React.SVGProps<SVGSVGElement>> } & ToneColors;

// Resolve a `kind` to its chip icon + colors, falling back to the neutral default.
export function getNotificationVisual(kind: string): NotificationVisual {
  const config = NOTIFICATION_TYPES[kind] ?? DEFAULT_NOTIFICATION_TYPE;
  return { Icon: config.Icon, ...TONE_COLORS[config.tone] };
}
